import * as React from 'react';
import { CheckCircleFilled, InfoCircleOutlined, ThunderboltFilled, WarningFilled } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Descriptions from 'antd/lib/descriptions';
import { AutomationAutoStagedRollout, HuaweiAppGallerySettings } from '../../types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Space from 'antd/lib/space';
import { AppComponentProps, Nullable } from '../../typescriptHelpers';
import { convertAndroidStagedRolloutPercentage, displayStringForRolloutPercentage } from '../../helpers/displayHelpers';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import Tooltip from 'antd/lib/tooltip';
import { selectAutomationById, selectIsReleaseRollback } from '@/selectors/selectors';
import { getRolloutDaysToPercentageFromAutomation } from '@/helpers';

interface HuaweiAppGalleryReleaseSettingsProps extends AppComponentProps {
    isSubmitAutomationDisabledMissingDefaults: boolean;
    appDefaultSettings?: HuaweiAppGallerySettings;
    releaseSettings?: HuaweiAppGallerySettings;
    bordered?: boolean;
    labelStyle?: React.CSSProperties;
    contentStyle?: React.CSSProperties;
    colon?: boolean;
    releaseId?: string;
    autoStagedRolloutAutomationId: Nullable<string>;
}

const HuaweiAppGalleryReleaseSettingsDescriptions: React.FC<HuaweiAppGalleryReleaseSettingsProps> = (
    props: HuaweiAppGalleryReleaseSettingsProps,
) => {
    const releaseSettings = props.releaseSettings;

    const autoStagedAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const customPhasedReleaseEnabledAndActive = useSelector((state: RootState) => {
        const isEnabled = autoStagedAutomation?.enabled;

        if (props.releaseId) {
            const isRollback = selectIsReleaseRollback(state, props.releaseId);

            return isEnabled && !isRollback;
        }

        return isEnabled;
    });

    const stagedRolloutDaysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedAutomation);

    const stagedRolloutEnabled = !!releaseSettings && releaseSettings.stagedRelease === true;
    const stagedRolloutEnabledText =
        releaseSettings ?
            releaseSettings.stagedRelease ?
                'Yes'
            :   'No'
        :   null;
    const stagedRolloutPercent =
        releaseSettings?.initialStagedRolloutPercentage ?
            displayStringForRolloutPercentage(
                convertAndroidStagedRolloutPercentage(releaseSettings.initialStagedRolloutPercentage),
            )
        :   null;
    const showStagedRolloutPercent =
        !!releaseSettings && releaseSettings.stagedRelease && !customPhasedReleaseEnabledAndActive;

    const autoStagedRolloutSequence = [];

    if (stagedRolloutDaysToPercent) {
        stagedRolloutDaysToPercent.forEach((percent, i) => {
            autoStagedRolloutSequence.push(`Day ${i + 1}: ${percent}%`);
        });
    }

    const customStagedRolloutAutomationEnabledButStagedRolloutSettingDisabled =
        customPhasedReleaseEnabledAndActive && !stagedRolloutEnabled;
    const settingsWarningText =
        props.isSubmitAutomationDisabledMissingDefaults ?
            `Runway is unable to perform your scheduled submit automation because your Huawei AppGallery phased release default is not set. Add release defaults to allow Runway to
    automatically submit as expected.`
        : customStagedRolloutAutomationEnabledButStagedRolloutSettingDisabled ?
            "Runway is unable to perform your configured custom phased release automation because phased release is not enabled. Change this default release method to phased release 'Yes' to allow Runway to perform your custom phased release."
        :   null;

    const needsSettingsConfirmation = stagedRolloutEnabled && customPhasedReleaseEnabledAndActive;
    const confirmationText =
        "Keep your phased release set to 'Yes' to allow Runway to proceed with your custom phased release automation.";

    const showCustomStagedRollout =
        stagedRolloutEnabled && customPhasedReleaseEnabledAndActive && stagedRolloutDaysToPercent;

    return (
        <Descriptions
            column={1}
            labelStyle={props.labelStyle}
            contentStyle={props.contentStyle}
            colon={props.colon}
            bordered={props.bordered}
        >
            <Descriptions.Item label="Phased release enabled">
                <Space direction="vertical">
                    {stagedRolloutEnabledText ?? <Text type="secondary">Not set</Text>}
                    {!!settingsWarningText && (
                        <Space align="start">
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                            <Text style={{ color: antGrey.grey7 }}>{settingsWarningText}</Text>
                        </Space>
                    )}
                    {needsSettingsConfirmation && (
                        <Space align="start">
                            <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />
                            <Text style={{ color: antGrey.grey7 }}>{confirmationText}</Text>
                        </Space>
                    )}
                </Space>
            </Descriptions.Item>
            {showStagedRolloutPercent && (
                <Descriptions.Item label="Initial phased release percentage">
                    {stagedRolloutPercent ?? <Text type="secondary">Not set</Text>}
                </Descriptions.Item>
            )}
            {showCustomStagedRollout && (
                <Descriptions.Item label="Custom phased release">
                    <Space>
                        <ThunderboltFilled style={{ color: runwayColors.primary }} />
                        <Text style={{ color: antGrey.grey7 }}>Automated custom phased release is enabled.</Text>
                        <Tooltip
                            title={
                                <Space direction="vertical">
                                    <Space direction="vertical" size={0}>
                                        {autoStagedRolloutSequence.map((day, i) => {
                                            return (
                                                <Text style={{ color: 'white' }} key={i}>
                                                    {day}
                                                </Text>
                                            );
                                        })}
                                    </Space>
                                    <Text style={{ color: 'white' }}>
                                        You can configure your custom phased release from automation settings.
                                    </Text>
                                </Space>
                            }
                        >
                            <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                        </Tooltip>
                    </Space>
                </Descriptions.Item>
            )}
        </Descriptions>
    );
};

export default HuaweiAppGalleryReleaseSettingsDescriptions;
